import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from "@angular/core";
import { AbstractControl, NgModel, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from "@angular/forms";
import { ClaimsTypes } from "@bbwt/modules/security/claims-types";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { genericSearchValues, searchStringValues } from "@mipro/classes/search-utlis";
import { ControlBase } from "@mipro/custom-controls";
import { SystemCodeType } from "@project/governingbody/system-code-type";
import { Diagnosis } from "./diagnosis";
import { DiagnosisService } from "./diagnosis.service";
import { GenericDiagnosisCodes } from "./generic-codes";
import { OsiicsFields } from "./osiics-fields";
import { MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";

@Component({
    selector: "diagnosis",
    templateUrl: "./diagnosis-control.component.html",
    styleUrls: ["./diagnosis-control.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DiagnosisControlComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: DiagnosisControlComponent,
            multi: true
        }
    ]
})
export class DiagnosisControlComponent extends ControlBase<Diagnosis> implements OnInit {
    private _diagnosisValue: Diagnosis;
    private _osiicsFields: OsiicsFields;
    private readonly _notApplicable = "N/A";
    private readonly _medicalBodyPart = "Medical";
    private readonly _bodySides = ["Left", "Right", "Bilateral", this._notApplicable];

    initializing = true;
    diagnosisSuggestions: Diagnosis[] = [];
    nhsTerminologyServerHealthy: boolean = true;
    osiicsCodeSuggestions: Diagnosis[] = [];
    bodyPartSuggestions: string[] = [];
    medicalSystemSuggestions: string[] = [];
    bodySideSuggestions: string[] = [];
    isAdvancedMode = false;

    @Input() layout: "two-columns" | "vertical" = "two-columns";

    @Input() defaultBodySideToNA = true;

    @Output() readonly nhsTerminologyServerHealthyResponse = new EventEmitter<boolean>();

    @ViewChildren(NgModel) diagnosisInputs: QueryList<NgModel>;

    constructor(
        private diagnosisService: DiagnosisService,
        private appOnlineService: AppOnlineStateService,
        private userHelperService: UserHelperService,
        private messageService: MessageService
    ) {
        super();
    }

    async ngOnInit() {
        try {
            this._osiicsFields = await this.diagnosisService.getOSIICSFields();
            if (this._osiicsFields?.medicalSystems && !this._osiicsFields.medicalSystems.includes(this._notApplicable)) {
                this._osiicsFields.medicalSystems.push(this._notApplicable);
            }

            this.osiicsCodeSuggestions = this._osiicsFields?.osiicsCodes ?? [];
            this.bodyPartSuggestions = this._osiicsFields?.bodyParts ?? [];
            this.medicalSystemSuggestions = this._osiicsFields?.medicalSystems ?? [];
        } finally {
            this.initializing = false;
        }
    }

    writeValue(obj: Diagnosis): void {
        this.diagnosisValue = obj;
    }

    createDefaultDiagnosisForm(): Diagnosis {
        return {
            id: "",
            code: "",
            isOSIICSCode: true,
            name: "",
            bodyPartName: "",
            medicalSystemName: "",
            bodySideName: ""
        };
    }

    validate(_: AbstractControl): ValidationErrors | null {
        if (!this.diagnosisValue || !this._diagnosisValue.name || !this.diagnosisValue.bodySideName) {
            return { invalid: true };
        }
        return null;
    }

    set diagnosisValue(diagnosis: Diagnosis | string) {
        if (typeof diagnosis === "string") {
            diagnosis = <Diagnosis>{ name: diagnosis };
        }

        const currentBodySide = this._diagnosisValue?.bodySideName;

        if (diagnosis && !diagnosis.medicalSystemName && diagnosis.name) {
            diagnosis = { ...diagnosis, medicalSystemName: this._notApplicable };
        }

        this._diagnosisValue = diagnosis;
        if (this._diagnosisValue) {
            this._diagnosisValue.bodySideName = currentBodySide;
            if (this.defaultBodySideToNA) {
                diagnosis.bodySideName = this._notApplicable;
            }
        }

        this.onChange(diagnosis);
    }

    get diagnosisValue(): Diagnosis {
        return this._diagnosisValue;
    }

    get isAppOnline$() {
        return this.appOnlineService.isAppOnline$;
    }

    get isOSIICSEnabled() {
        return (
            (+this.userHelperService.findFirstValue(ClaimsTypes.OrganisationSystemCode) & SystemCodeType.OSIICS) === SystemCodeType.OSIICS
        );
    }

    get medicalSystemName() {
        return this.diagnosisValue?.medicalSystemName;
    }

    set medicalSystemName(medicalSystemName: string) {
        if (!medicalSystemName) {
            this.diagnosisValue = null;
            return;
        }

        this.createDiagnosisValue();

        if (!this.diagnosisValue || this.diagnosisValue.isOSIICSCode) {
            this.diagnosisValue = this.createDefaultDiagnosisForm();
        }

        this.diagnosisValue.medicalSystemName = medicalSystemName;
        if (this.diagnosisValue.medicalSystemName !== this._notApplicable) {
            this.diagnosisValue.bodyPartName = this._medicalBodyPart;
        }
    }

    get bodyPartName() {
        return this.diagnosisValue?.bodyPartName;
    }

    set bodyPartName(bodyPartName: string) {
        if (!bodyPartName) {
            this.diagnosisValue = null;
            return;
        }

        this.createDiagnosisValue();

        if (
            !this.diagnosisValue ||
            (this.diagnosisValue.isOSIICSCode && this.diagnosisValue.code !== GenericDiagnosisCodes.RoutineDiagnosisCode)
        ) {
            if (this._diagnosisValue == null) {
                this._diagnosisValue = this.diagnosisValue ?? this.createDefaultDiagnosisForm();
            }
            this._diagnosisValue.isOSIICSCode = true;
        }

        this.diagnosisValue.bodyPartName = bodyPartName;
        this.diagnosisValue.medicalSystemName = null;
        if (this.diagnosisValue.bodyPartName !== this._medicalBodyPart) {
            this.diagnosisValue.medicalSystemName = this._notApplicable;
        }
    }

    get bodySideName() {
        return this.diagnosisValue?.bodySideName;
    }

    set bodySideName(bodySideName: string) {
        this.createDiagnosisValue();

        if (this._diagnosisValue == null) {
            this._diagnosisValue = this.diagnosisValue ?? this.createDefaultDiagnosisForm();
        }

        this._diagnosisValue = { ...this._diagnosisValue, bodySideName };
        this.onChange(this._diagnosisValue);
    }

    async searchDiagnoses(term: string) {
        const result = await this.diagnosisService.searchDiagnoses(term);
        this.diagnosisSuggestions = result.results;
        this.nhsTerminologyServerHealthy = result.nhsTerminologyServerHealthy;
        this.nhsTerminologyServerHealthyResponse.emit(this.nhsTerminologyServerHealthy);
        if (!this.nhsTerminologyServerHealthy && (await this.isAppOnline$)) {
            this.messageService.add(
                Message.Warning(
                    "SNOMED Diagnosis Search is currently not available, if these are required please save the Episode as a Draft and try again later."
                )
            );
        }
    }

    searchOSIICSCodes(term: string) {
        this.osiicsCodeSuggestions = genericSearchValues(this._osiicsFields?.osiicsCodes, term, osiics => osiics.code);
    }

    searchMedicalSystems(term: string) {
        this.medicalSystemSuggestions = searchStringValues(this._osiicsFields?.medicalSystems, term);
    }

    searchBodyParts(term: string) {
        this.bodyPartSuggestions = searchStringValues(this._osiicsFields?.bodyParts, term);
    }

    searchBodySides(term: string) {
        this.bodySideSuggestions = searchStringValues(this._bodySides, term);
    }

    reset() {
        this.diagnosisInputs?.forEach(ngModel => {
            ngModel.control.markAsUntouched();
            ngModel.control.markAsPristine();
        });
    }

    markAsDirtyTouched() {
        this.diagnosisInputs?.forEach(ngModel => {
            ngModel.control.markAsDirty();
            ngModel.control.markAsTouched();
        });
    }

    private createDiagnosisValue() {
        this.diagnosisValue = this.diagnosisValue ? { ...this.diagnosisValue } : null;
    }

    toggleView() {
        this.isAdvancedMode = !this.isAdvancedMode;
    }
}
